<template>
    <section class="rulebook-versions">
        <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-3">
            <div>
                <h5 class="mb-1">기준 버전</h5>
                <p class="text-muted mb-0">
                    최근 50개 버전입니다. 잘못된 버전은 이전 버전으로 복귀하거나 다시 켜지지 않도록 차단합니다.
                </p>
            </div>
            <b-button size="sm"
                      variant="outline-secondary"
                      class="mt-2 mt-md-0"
                      :disabled="loading"
                      @click="loadVersions"
            >
                <b-spinner v-if="loading" small class="mr-1" />새로고침
            </b-button>
        </div>

        <b-alert v-if="error" show variant="danger" class="d-flex align-items-center justify-content-between">
            <span>{{ error }}</span>
            <b-button size="sm" variant="outline-danger" @click="loadVersions">다시 불러오기</b-button>
        </b-alert>

        <div v-if="loading && versions.length === 0" class="text-center text-muted py-5">
            <b-spinner small class="mr-2" />버전 목록을 불러오는 중입니다.
        </div>

        <b-table v-else
                 :items="versions"
                 :fields="fields"
                 responsive
                 hover
                 :show-empty="!error"
                 class="version-table"
                 empty-text="아직 발행된 기준 버전이 없습니다."
                 @row-clicked="openDetail"
        >
            <template #cell(versionNo)="row">
                <strong>{{ row.item.versionNo === null ? '#' + row.item.id : 'v' + row.item.versionNo }}</strong>
            </template>
            <template #cell(status)="row">
                <b-badge :variant="versionStatus(row.item).variant">{{ versionStatus(row.item).label }}</b-badge>
            </template>
            <template #cell(origin)="row">
                {{ versionOrigin(row.item).label }}
            </template>
            <template #cell(validationDecision)="row">
                {{ versionDecision(row.item).label }}
            </template>
            <template #cell(principleCount)="row">
                {{ row.item.principleCount === null ? '—' : row.item.principleCount }}
            </template>
            <template #cell(createdAt)="row">
                {{ formatDate(row.item.createdAt) }}
            </template>
            <template #cell(actions)="row">
                <b-button size="sm" variant="outline-secondary" @click.stop="openDetail(row.item)">
                    상세 보기
                </b-button>
                <b-button v-if="showRollback(row.item)"
                          size="sm"
                          variant="outline-primary"
                          class="ml-1"
                          :disabled="mutationsDisabled"
                          @click.stop="confirmRollback(row.item)"
                >
                    복귀
                </b-button>
                <b-button v-if="showBlock(row.item)"
                          size="sm"
                          variant="outline-danger"
                          class="ml-1"
                          :disabled="mutationsDisabled"
                          @click.stop="openBlock(row.item)"
                >
                    차단
                </b-button>
                <span v-if="row.item.isActive" class="text-muted small ml-1">
                    활성 버전은 먼저 안전 버전으로 복귀한 뒤 차단할 수 있습니다.
                </span>
            </template>
        </b-table>

        <b-modal v-model="detailVisible"
                 :title="detailTitle"
                 size="lg"
                 ok-only
                 ok-title="닫기"
                 scrollable
        >
            <div v-if="detailLoading" class="text-center text-muted py-4">
                <b-spinner small class="mr-2" />버전 본문을 불러오는 중입니다.
            </div>
            <b-alert v-else-if="detailError" show variant="danger" class="d-flex align-items-center justify-content-between">
                <span>{{ detailError }}</span>
                <b-button size="sm" variant="outline-danger" @click="loadDetail">다시 불러오기</b-button>
            </b-alert>
            <template v-else-if="detail">
                <div class="detail-meta mb-3">
                    <b-badge :variant="versionStatus(detail).variant" class="mr-2">
                        {{ versionStatus(detail).label }}
                    </b-badge>
                    <span class="text-muted small">
                        원본 {{ versionOrigin(detail).label }} · 검증 {{ versionDecision(detail).label }}
                        · 생성 {{ formatDate(detail.createdAt) }} · 원칙 {{ detail.principleCount }}개
                    </span>
                </div>

                <h6 class="mb-2">기준 본문</h6>
                <pre class="content-block" v-text="detail.content" />

                <h6 class="mt-4 mb-2">원칙 목록</h6>
                <div v-if="detail.principles.length === 0" class="text-muted small">등록된 원칙이 없습니다.</div>
                <div v-for="principle in detail.principles" :key="principle.ruleCode" class="principle-row">
                    <div class="d-flex align-items-center flex-wrap mb-1">
                        <b-badge variant="light" class="rule-code mr-2">{{ principle.ruleCode }}</b-badge>
                        <strong>{{ principle.title }}</strong>
                    </div>
                    <div class="text-muted small">{{ principle.ruleText }}</div>
                </div>
            </template>
        </b-modal>

        <b-modal v-model="blockVisible"
                 title="버전 차단"
                 ok-title="차단"
                 cancel-title="취소"
                 ok-variant="danger"
                 :ok-disabled="mutationsDisabled"
                 @ok.prevent="submitBlock"
        >
            <p class="mb-3">
                {{ blockTargetLabel }} 을(를) 차단하면 같은 본문의 후보가 다시 만들어지거나 켜지지 않습니다.
            </p>
            <p class="mb-3 text-danger">
                차단은 해제할 수 없습니다. 이 버전으로는 복귀할 수 없으며 되돌릴 수 없습니다.
            </p>
            <label for="rulebook-block-reason" class="field-label">차단 사유</label>
            <b-form-textarea id="rulebook-block-reason"
                             v-model="blockReason"
                             rows="3"
                             placeholder="예: 증빙 인정 범위가 과도하게 넓어짐"
                             :state="blockReasonError ? false : null"
            />
            <div v-if="blockReasonError" class="text-danger small mt-1">{{ blockReasonError }}</div>
        </b-modal>
    </section>
</template>

<script>
import {
    apiErrorMessage,
    canBlockVersion,
    canRollbackVersion,
    formatKstDateTime,
    normalizeVersionDetail,
    normalizeVersionList,
    originMeta,
    validationDecisionMeta,
    versionStatusMeta,
} from './rulebook-presentation';

const VERSION_LIMIT = 50;

export default {
    name: 'RulebookVersionsTab',
    data() {
        return {
            loading: false,
            error: '',
            versions: [],
            head: {activeVersionId: null, activeVersion: null},
            fields: [
                {key: 'versionNo', label: '버전'},
                {key: 'status', label: '상태'},
                {key: 'origin', label: '원본'},
                {key: 'validationDecision', label: '검증'},
                {key: 'principleCount', label: '원칙 수'},
                {key: 'createdAt', label: '생성 시각'},
                {key: 'actions', label: '동작'},
            ],
            versionsRequestId: 0,
            detailVisible: false,
            detailLoading: false,
            detailError: '',
            detailVersionId: null,
            detail: null,
            blockVisible: false,
            blockTarget: null,
            blockReason: '',
            blockReasonError: '',
            pendingVersionId: null,
        };
    },
    computed: {
        activeVersionNo() {
            // head 가 준 번호가 authoritative 다. 활성 버전이 최근 50개 밖으로 밀려도 v번호를 잃지 않는다.
            const headVersion = this.head.activeVersion;
            if (headVersion && headVersion.versionNo !== null) return headVersion.versionNo;
            const versions = this.versions || [];
            const active = versions.find((item) => item.id === this.head.activeVersionId);
            return active && active.versionNo !== null ? active.versionNo : null;
        },
        activeVersionLabel() {
            if (this.activeVersionNo !== null) return `v${this.activeVersionNo}`;
            if (this.head.activeVersionId !== null) return `#${this.head.activeVersionId}`;
            return '없음';
        },
        detailTitle() {
            if (!this.detail || this.detail.versionNo === null) return '기준 버전 상세';
            return `기준 버전 v${this.detail.versionNo}`;
        },
        blockTargetLabel() {
            return this.blockTarget ? this.versionLabel(this.blockTarget) : '';
        },
        /** 최신 목록을 확인하지 못한 동안(로딩 중·조회 실패)에는 stale 행을 조작하지 못하게 막는다. */
        mutationsDisabled() {
            return this.pendingVersionId !== null || this.loading || Boolean(this.error);
        },
    },
    created() {
        this.loadVersions();
    },
    methods: {
        versionLabel(version) {
            return version.versionNo === null ? `#${version.id}` : `v${version.versionNo}`;
        },
        versionStatus(version) {
            return versionStatusMeta(version.status);
        },
        versionOrigin(version) {
            return originMeta(version.origin);
        },
        versionDecision(version) {
            return validationDecisionMeta(version.validationDecision);
        },
        formatDate(value) {
            return formatKstDateTime(value);
        },
        showRollback(version) {
            return canRollbackVersion(version);
        },
        showBlock(version) {
            return canBlockVersion(version);
        },
        notify(message, variant) {
            this.$bvToast.toast(message, {title: '기준 버전', variant, solid: true});
        },
        async loadVersions() {
            // 사용자의 새로고침과 변경 뒤 resync 가 겹칠 수 있다. 마지막으로 시작한 요청만 반영한다.
            this.versionsRequestId += 1;
            const requestId = this.versionsRequestId;
            this.loading = true;
            this.error = '';
            try {
                const {data} = await this.$axios.get('/eval-prompt/rulebook/versions', {
                    params: {limit: VERSION_LIMIT},
                });
                if (this.versionsRequestId !== requestId) return false;
                const normalized = normalizeVersionList(data);
                this.versions = normalized.versions;
                this.head = normalized.head;
                return true;
            } catch (err) {
                if (this.versionsRequestId !== requestId) return false;
                // 재조회 실패는 직전 목록을 지우지 않는다.
                this.error = apiErrorMessage(err, '버전 목록을 불러오지 못했습니다.');
                return false;
            } finally {
                if (this.versionsRequestId === requestId) this.loading = false;
            }
        },
        async openDetail(version) {
            this.detailVersionId = version.id;
            this.detail = null;
            this.detailError = '';
            this.detailVisible = true;
            await this.loadDetail();
        },
        async loadDetail() {
            if (this.detailVersionId === null) return;
            const versionId = this.detailVersionId;
            this.detailLoading = true;
            this.detailError = '';
            try {
                const {data} = await this.$axios.get(`/eval-prompt/rulebook/versions/${versionId}`);
                if (this.detailVersionId !== versionId) return;
                this.detail = normalizeVersionDetail(data);
            } catch (err) {
                if (this.detailVersionId !== versionId) return;
                this.detailError = apiErrorMessage(err, '버전 본문을 불러오지 못했습니다.');
            } finally {
                if (this.detailVersionId === versionId) this.detailLoading = false;
            }
        },
        /**
         * 변경 뒤에는 목록과 개요를 모두 다시 읽어 stale 표시를 남기지 않는다.
         * 반환값은 "최신 목록을 실제로 반영했는지" 다 — 실패하면 호출부가 stale 상태를 정리해야 한다.
         */
        async resync() {
            const refreshed = await this.loadVersions();
            this.$emit('refresh-overview');
            return refreshed;
        },
        async confirmRollback(version) {
            if (this.mutationsDisabled) return;
            // 확인 모달에 보인 활성본과 낙관 잠금 값이 반드시 같아야 한다. 모달이 열린 동안
            // 목록 재조회가 head 를 바꿔도 사용자가 확인하지 않은 버전을 차단하면 안 된다.
            const expectedActiveVersionId = this.head.activeVersionId;
            const activeLabel = this.activeVersionLabel;
            const targetId = version.id;
            const targetLabel = this.versionLabel(version);
            this.pendingVersionId = targetId;
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `현재 활성 ${activeLabel} → ${targetLabel} 로 되돌립니다.`
                    + ' 현재 활성 버전은 차단 상태로 남습니다. 계속할까요?',
                    {title: '기준 버전 복귀', okTitle: '복귀', cancelTitle: '취소', okVariant: 'primary'},
                );
                if (!confirmed) return;
                await this.$axios.post(`/eval-prompt/rulebook/versions/${targetId}/activate`, {
                    rollback: true,
                    expected_active_version_id: expectedActiveVersionId,
                });
                this.notify(`${targetLabel} 로 복귀했습니다.`, 'success');
                await this.resync();
            } catch (err) {
                this.notify(apiErrorMessage(err, '버전을 복귀하지 못했습니다.'), 'danger');
                await this.resync();
            } finally {
                this.pendingVersionId = null;
            }
        },
        closeBlockModal() {
            this.blockVisible = false;
            this.blockTarget = null;
            this.blockReason = '';
            this.blockReasonError = '';
        },
        /** 재조회 뒤 차단 대상이 사라졌거나 더 이상 차단할 수 없으면 모달을 닫는다. */
        syncBlockTarget(versionId) {
            const fresh = (this.versions || []).find((item) => item.id === versionId);
            if (fresh && canBlockVersion(fresh)) {
                this.blockTarget = fresh;
                return;
            }
            this.closeBlockModal();
        },
        openBlock(version) {
            this.blockTarget = version;
            this.blockReason = '';
            this.blockReasonError = '';
            this.blockVisible = true;
        },
        async submitBlock() {
            if (this.mutationsDisabled || !this.blockTarget) return;
            const reason = this.blockReason.trim();
            if (!reason) {
                this.blockReasonError = '차단 사유를 입력해주세요.';
                return;
            }
            this.blockReasonError = '';
            const version = this.blockTarget;
            this.pendingVersionId = version.id;
            try {
                await this.$axios.post(`/eval-prompt/rulebook/versions/${version.id}/block`, {reason});
                this.blockVisible = false;
                this.notify(`${this.versionLabel(version)} 를 차단했습니다.`, 'success');
                await this.resync();
            } catch (err) {
                this.notify(apiErrorMessage(err, '버전을 차단하지 못했습니다.'), 'danger');
                // 실패는 대개 다른 관리자가 상태를 바꾼 결과다. 최신 상태로 맞추고
                // 대상이 더 이상 차단 대상이 아니면 같은 실패를 반복하지 않도록 모달을 닫는다.
                // 재조회까지 실패하면 최신 상태를 확인할 방법이 없으므로 무조건 닫는다.
                if (await this.resync()) this.syncBlockTarget(version.id);
                else this.closeBlockModal();
            } finally {
                this.pendingVersionId = null;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.field-label {
    color: #52606d;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

::v-deep .version-table th {
    background: #f8f9fa;
    color: #52606d;
    font-size: 0.8rem;
    white-space: nowrap;
}

::v-deep .version-table td {
    vertical-align: middle;
}

.content-block {
    background: #f8f9fa;
    border: 1px solid #e5e9ee;
    border-radius: 6px;
    color: #354052;
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0;
    max-height: 22rem;
    overflow: auto;
    padding: 1rem;
    white-space: pre-wrap;
    word-break: break-word;
}

.principle-row {
    border-top: 1px solid #edf1f5;
    padding: 0.65rem 0;

    &:first-of-type {
        border-top: 0;
    }
}

.rule-code {
    border: 1px solid #ccd6e0;
    color: #364152;
    font-family: monospace;
}

.detail-meta {
    border-bottom: 1px solid #edf1f5;
    padding-bottom: 0.75rem;
}
</style>
